'use client'

import { isValidPhoneNumber } from 'libphonenumber-js'


import { MInput } from '@/_components/medante/m-input'
import { MSelect, type MSelectOption } from '@/_components/medante/m-select'
import { getEUCountries } from '@/_lib/utils'

import { ERR } from '../_utils/patient-form.constants'
import { getBirthNumberRegisterOptions } from '../_utils/patient-form.validation'

import { APatientInfoSectionHeading } from './a-patient-info-section-heading'

import type { PatientFormValues } from '../_types/patient-form.types'
import type { Control, FieldErrors, UseFormRegister } from 'react-hook-form'

type APatientInfoPersonalDetailsSectionProps = {
	register: UseFormRegister<PatientFormValues>
	control: Control<PatientFormValues>
	errors: FieldErrors<PatientFormValues>
	isChildOrder: boolean
	showInsurance: boolean
	isEUInsurance: boolean
	insuranceSelectOptions: MSelectOption[]
}

export function APatientInfoPersonalDetailsSection({
	register,
	control,
	errors,
	isChildOrder,
	showInsurance,
	isEUInsurance,
	insuranceSelectOptions
}: APatientInfoPersonalDetailsSectionProps) {
	return (
		<section className='mb-8'>
			<APatientInfoSectionHeading>Osobné údaje pacienta</APatientInfoSectionHeading>

			<div className='mb-3 grid grid-cols-1 gap-2.5 md:grid-cols-3 md:gap-4'>
				<MInput
					label='Meno'
					required
					error={errors.firstName?.message}
					registration={register('firstName', { required: ERR.firstName })}
				/>

				<MInput
					label='Priezvisko'
					required
					error={errors.lastName?.message}
					registration={register('lastName', { required: ERR.lastName })}
				/>

				<MInput
					label='Rodné číslo'
					required={!isEUInsurance}
					error={errors.birthNumber?.message}
					registration={register('birthNumber', getBirthNumberRegisterOptions(isEUInsurance))}
				/>
			</div>

			{showInsurance && isChildOrder && (
				<div className='mb-3'>
					<MSelect<PatientFormValues>
						name='insurance'
						control={control}
						rules={{ required: ERR.insurance }}
						label='Poisťovňa'
						required
						error={errors.insurance?.message}
						options={insuranceSelectOptions}
					/>
				</div>
			)}

			{!isChildOrder && (
				<div className='mb-3 grid grid-cols-1 gap-2.5 md:grid-cols-3 md:gap-4'>
					<MInput
						label='Email'
						type='email'
						required
						error={errors.email?.message}
						registration={register('email', {
							required: ERR.email.required,
							pattern: {
								value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
								message: ERR.email.invalid
							}
						})}
					/>

					<MInput<PatientFormValues, 'phone'>
						type='phone'
						name='phone'
						control={control}
						rules={{
							required: ERR.phone.required,
							validate: v => (v && isValidPhoneNumber(v)) || ERR.phone.invalid
						}}
						label='Telefonický kontakt'
						required
						error={errors.phone?.message}
					/>

					{showInsurance && (
						<MSelect<PatientFormValues>
							name='insurance'
							control={control}
							rules={{ required: ERR.insurance }}
							label='Poisťovňa'
							required
							error={errors.insurance?.message}
							options={insuranceSelectOptions}
						/>
					)}
				</div>
			)}

			{isEUInsurance && (
				<div className='mb-3 grid grid-cols-1 gap-2.5 md:grid-cols-3 md:gap-4'>
					<MInput
						label='Identifikačné číslo poistenca'
						type='tel'
						required
						error={errors.euInsuranceNumber?.message}
						registration={register('euInsuranceNumber', {
							required: ERR.euInsuranceNumber
						})}
					/>

					<MInput
						label='Zmluvné poistenie'
						type='tel'
						required
						error={errors.contractualInsurance?.message}
						registration={register('contractualInsurance', {
							required: ERR.contractualInsurance
						})}
					/>

					<MSelect<PatientFormValues>
						name='countryId'
						control={control}
						rules={{ required: ERR.countryId }}
						label='Krajina'
						required
						error={errors.countryId?.message}
						options={getEUCountries()}
					/>
				</div>
			)}
		</section>
	)
}
